import type {
  BrandSpecialUserSegmentIds,
  PriceId,
  ProductWeight,
  SummarizedBrandSpecial,
} from '@iheartjane/types';
import { ProductWeightsEnum } from '@iheartjane/types';
import type { DeepReadonly } from 'ts-essentials';

import type { LabResult } from '@jane/shared/types';

import type { Photo } from './photo';
import type {
  BrandSpecialPrices,
  Effect,
  Flavor,
  ProductKind,
  ProductLineage,
  ProductType,
} from './product';

interface InventoryTax {
  apply_to_discounted_price: boolean;
  apply_to_non_cannabis_item: boolean;
  is_taxable: boolean;
  rate: number;
  title: string;
}

interface Inventory {
  bulk: boolean;
  equivalency: number;
  id: number;
  menu_product_id: number;
  price_id: string | null;
  tax?: InventoryTax | null;
}

interface InventoryPotency {
  cbd_potency: number | null;
  price_id: string | null;
  tac_potency: number | null;
  thc_potency: number | null;
  thca_potency: number | null;
}

export function isProductWeight(s: string): s is ProductWeight {
  return (ProductWeightsEnum as readonly string[]).includes(s);
}

export function isPriceId(s: string): s is PriceId {
  return s === 'each' || isProductWeight(s);
}

interface PhotoWeight {
  id: number;
  menu_product_id: number;
  public_image_id: number;
  weights?: PriceId[];
}

export interface ProductWeightSpecial {
  discount_amount: string | null;
  discount_percent: string | null;
  discount_price: string | null;
  discount_type: string | null;
  price: number;
  special_custom_badge?: string | null;
  special_id: number;
}

interface AbbreviatedMenuProduct {
  brand: string | null;
  brand_subtype: string | null;
  id: number;
  kind: ProductKind;
  name: string;
  price_each: string | null;
  price_eighth_ounce: string | null;
  price_gram: string | null;
  price_half_gram: string | null;
  price_half_ounce: string | null;
  price_id?: string | null;
  price_ounce: string | null;
  price_quarter_ounce: string | null;
  price_two_gram: string | null;
  product_brand_id?: number | null;
  product_id: number;
  type: ProductType;
}

export type MenuProduct = DeepReadonly<
  AbbreviatedMenuProduct & {
    aggregate_rating?: number | null;
    allergens?: string[];
    allow_multiple_flower_count: boolean;
    amount?: string | null;
    applicable_brand_special_ids?: number[] | null;
    applicable_brand_specials_excluded_user_segments?: BrandSpecialUserSegmentIds;
    applicable_brand_specials_included_user_segments?: BrandSpecialUserSegmentIds;
    applicable_bundle_brand_special_ids?: {
      discount: number[];
      qualify: number[];
    } | null;
    applicable_bundle_brand_specials?: {
      discount: number[];
      qualify: number[];
    } | null;
    applicable_special_ids?: number[];
    available_weights?: string[];
    best_seller_rank?: number | null;
    brand_discount_label?: string | null;
    brand_discount_title?: string | null;
    brand_discounted_price?: number | null;
    brand_special_has_segment_rules?: boolean | null;
    brand_special_id?: number | null;
    brand_special_prices?: BrandSpecialPrices | null;
    brand_specials?: SummarizedBrandSpecial[] | null;
    category: ProductLineage | null;
    cbd_dosage_milligrams?: number | null;
    description: string;
    discounted_price_each: string | null;
    discounted_price_eighth_ounce: string | null;
    discounted_price_gram: string | null;
    discounted_price_half_gram: string | null;
    discounted_price_half_ounce: string | null;
    discounted_price_ounce: string | null;
    discounted_price_quarter_ounce: string | null;
    discounted_price_two_gram: string | null;
    dosage?: string | null;
    effects: Effect[];
    flavors: Flavor[];
    has_brand_discount?: boolean | null;
    image_urls: string[];
    ingredients?: string[];
    inventories?: Inventory[];
    inventory_potencies?: InventoryPotency[];
    lab_result_urls?: {
      lab_result_url: string | null;
      price_id: string | null;
    }[];
    lab_results?: LabResult[];
    max_cart_quantity: number;
    net_weight_grams: number | null;
    operator_store_rank?: number | null;
    percent_cbd: number | null;
    percent_tac?: number | null;
    percent_thc: number | null;
    percent_thca?: number | null;
    percentCBDA?: number;
    percentTAC?: number;
    percentTHCA?: number;
    photo_weights?: PhotoWeight[];
    photos: Photo[];
    primaryPercentCBD?: number;
    primaryPercentTHC?: number;
    quantity_units: string | null;
    quantity_value: number | null;
    recommended?: boolean;
    review_count?: number | null;
    root_subtype: string | null;
    special_amount?: string | null;
    special_custom_badge?: string | null;
    special_id: number | null;
    special_price_each?: ProductWeightSpecial | null;
    special_price_eighth_ounce?: ProductWeightSpecial | null;
    special_price_gram?: ProductWeightSpecial | null;
    special_price_half_gram?: ProductWeightSpecial | null;
    special_price_half_ounce?: ProductWeightSpecial | null;
    special_price_ounce?: ProductWeightSpecial | null;
    special_price_quarter_ounce?: ProductWeightSpecial | null;
    special_price_two_gram?: ProductWeightSpecial | null;
    special_title: string | null;
    store_notes: string | null;
    terpenes?: Record<string, string>[];
    thc_dosage_milligrams?: number | null;
    url_slug: string;
    visible: boolean;
  }
>;
